import { createContext, useContext, useId } from "react";
import type { ReactNode } from "react";
import { classNames } from "./internal/class-names";
import { OperationalIcon } from "./operational-icon";

/**
 * What a `Field` hands down to whichever control it wraps. Controls merge it
 * with their own props (own props always win) so a caller can always override.
 */
export interface FieldBinding {
  readonly controlId: string;
  readonly describedBy: string | undefined;
  readonly invalid: boolean;
  readonly required: boolean;
  readonly disabled: boolean;
}

const FieldContext = createContext<FieldBinding | null>(null);

/** Read the enclosing `Field` binding. Returns null outside a `Field`. */
export function useFieldBinding(): FieldBinding | null {
  return useContext(FieldContext);
}

export interface ControlAriaOverrides {
  readonly id?: string | undefined;
  readonly required?: boolean | undefined;
  readonly disabled?: boolean | undefined;
  readonly describedBy?: string | undefined;
  readonly invalid?: boolean | undefined;
}

export interface ResolvedControlAria {
  readonly id: string | undefined;
  readonly required: boolean | undefined;
  readonly disabled: boolean | undefined;
  readonly describedBy: string | undefined;
  readonly invalid: "true" | undefined;
}

/**
 * Merges a `Field` binding with a control's own props. `aria-describedby` is
 * additive so a control keeps its own description while gaining the field hint
 * and error ids.
 */
export function resolveControlAria(
  binding: FieldBinding | null,
  own: ControlAriaOverrides,
): ResolvedControlAria {
  const describedBy = classNames(own.describedBy, binding?.describedBy);
  const invalid = own.invalid ?? binding?.invalid ?? false;

  return {
    describedBy: describedBy === "" ? undefined : describedBy,
    disabled: own.disabled ?? binding?.disabled,
    id: own.id ?? binding?.controlId,
    invalid: invalid ? "true" : undefined,
    required: own.required ?? binding?.required,
  };
}

export interface FieldProps {
  /** Visible label text. Always rendered — never replaced by a placeholder. */
  readonly label: ReactNode;
  /** Optional supporting text, associated through `aria-describedby`. */
  readonly hint?: ReactNode;
  /**
   * Resolved, human-readable error text. Stores keep error *codes*; the calling
   * view maps a code to text before passing it here.
   */
  readonly error?: ReactNode;
  readonly required?: boolean;
  readonly disabled?: boolean;
  /** Use an existing control id instead of a generated one. */
  readonly controlId?: string;
  readonly className?: string;
  readonly children: ReactNode;
}

export function Field({
  children,
  className,
  controlId,
  disabled = false,
  error,
  hint,
  label,
  required = false,
}: FieldProps): ReactNode {
  const generatedId = useId();
  const id = controlId ?? `${generatedId}-control`;
  const hintId = `${generatedId}-hint`;
  const errorId = `${generatedId}-error`;
  const hasHint = hint !== undefined && hint !== null && hint !== false;
  const hasError = error !== undefined && error !== null && error !== false;

  const describedBy = classNames(hasHint && hintId, hasError && errorId);
  const binding: FieldBinding = {
    controlId: id,
    describedBy: describedBy === "" ? undefined : describedBy,
    disabled,
    invalid: hasError,
    required,
  };

  return (
    <div className={classNames("thw-field", hasError && "thw-field--invalid", className)}>
      <label className="thw-field__label" htmlFor={id}>
        {label}
        {required ? (
          <span aria-hidden="true" className="thw-field__required">
            {" *"}
          </span>
        ) : null}
      </label>
      {hasHint ? (
        <p className="thw-field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      <FieldContext.Provider value={binding}>{children}</FieldContext.Provider>
      {hasError ? (
        <p className="thw-field__error" id={errorId} role="alert">
          <OperationalIcon name="warning" />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}

export const FormField = Field;
